// <ErrorBoundary> — contains render-time exceptions to a single page area.
//
// Before this existed a thrown error during render (e.g. dereferencing an
// undefined `rule.group_match.group_name`) propagated to the root and React
// unmounted the entire tree, blanking the whole SPA. Wrapping the admin
// <Outlet/> means a crash now shows a localized "Something went wrong" card
// while the topbar, sidebar, and navigation stay interactive — the user can
// route away or reload.
//
// React error boundaries MUST be class components: there is no hook
// equivalent for componentDidCatch / getDerivedStateFromError.

import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import "./ErrorBoundary.css";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback. Receives the caught error + a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
    this.handleReload = this.handleReload.bind(this);
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for local debugging / log scraping. We avoid a
    // toast here because the boundary may catch during the toast provider's
    // own subtree; the contained card is the user-visible signal.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] render exception:", error, info.componentStack);
  }

  reset(): void {
    this.setState({ error: null });
  }

  handleReload(): void {
    window.location.reload();
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) {
        return this.props.fallback(error, this.reset);
      }
      return (
        <div className="error-boundary-card" role="alert">
          <h2 className="error-boundary-card__title">
            Something went wrong on this page
          </h2>
          <p className="error-boundary-card__body">
            An unexpected error stopped this view from rendering. The rest of
            the app is still available — you can navigate away, or reload to try
            again.
          </p>
          <button
            type="button"
            className="error-boundary-card__action"
            onClick={this.handleReload}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
